/**
 * Anderra's map layer config, carried from the live call into the after-call
 * view (owner report 2026-09-23): "When a call ends... the layers on the map
 * should be locked in and anderras choices - the officers manupulations, and
 * the callers additions, should all be set in stone and saved - even for the
 * after call".
 *
 * The config lived only in useMapLayerConfig's ref and the map iframe, so the
 * after-call map had nothing to show: it sent an empty scope (clearing every
 * data layer) and asked the LLM again, which could choose different layers.
 * It is now kept in aiAnalysis.mapLayerConfig and saved in full_state by the
 * same writers as the rest of the call's analysis.
 */

import type { MapLayerConfigSnapshot } from '@/store/slices/aiAnalysisTypes'

type JsonRecord = Record<string, unknown>

export function isMapLayerConfigSnapshot(value: unknown): value is MapLayerConfigSnapshot {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const record = value as JsonRecord
  return Array.isArray(record.datasets) && Array.isArray(record.entityLocations)
}

export function selectMapLayerConfigForPersistence(
  analysis: { mapLayerConfig?: unknown } | null | undefined,
): { mapLayerConfig: MapLayerConfigSnapshot | null } {
  const config = analysis?.mapLayerConfig
  return { mapLayerConfig: isMapLayerConfigSnapshot(config) ? config : null }
}

/**
 * A writer that never saw Anderra apply a config (a report page before
 * hydration, a sync from before the map opened) must not erase the saved one.
 */
export function preserveSavedMapLayerConfig(
  target: JsonRecord,
  existing: JsonRecord | null | undefined,
): void {
  if (isMapLayerConfigSnapshot(target.mapLayerConfig)) return
  if (isMapLayerConfigSnapshot(existing?.mapLayerConfig)) {
    target.mapLayerConfig = existing!.mapLayerConfig
  }
}
